import { events } from "@constants/tracking";
import { SkillDetailDialog } from "@memorang/ui/components/SkillDetailDialog";
import { trackCustomEvent } from "analytics";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSkillDetail } from "../hooks/use-skill-detail";
import { useSkillRecommendations } from "../hooks/useSkillRecommendations";
import type { RecommendedSkillTask } from "../recommendations/adapters/dashboard-task-types";

type Props = {
	skillId?: string;
	onClose: () => void;
	onStartPractice: (task: RecommendedSkillTask) => void;
	startingPractice?: boolean;
};

const SkillDetailDialogContainer = ({
	skillId,
	onClose,
	onStartPractice,
	startingPractice,
}: Props) => {
	const { t } = useTranslation();
	const detail = useSkillDetail(skillId);
	const { entries, isLoading, startTest, startingTest } =
		useSkillRecommendations({
			skill: detail?.skill,
			levelKnown: detail?.levelKnown ?? false,
		});
	const [selectedEntryId, setSelectedEntryId] = useState<string>();

	// biome-ignore lint/correctness/useExhaustiveDependencies: fire once per opened skill, keyed by title so re-renders of the (unmemoized) detail object don't re-track
	useEffect(() => {
		if (detail) {
			trackCustomEvent({
				eventName: events.skillDetailOpened,
				skill: detail.title,
				mode: detail.levelKnown ? "known" : "unknown",
			});
		}
		setSelectedEntryId(undefined);
	}, [detail?.title]);

	if (!detail) {
		return null;
	}

	const selected =
		entries.find(({ id }) => id === selectedEntryId) ?? entries[0];
	const starting = startingTest || startingPractice;

	const handleStart = () => {
		if (!selected) {
			return;
		}
		onClose();
		if (selected.start === "test") {
			startTest(selected.examId);
		} else {
			onStartPractice(selected.task);
		}
	};

	const sessionSection =
		isLoading || entries.length > 0
			? {
					sessionsLabel: t("dashboard.recommendedTasks.sectionTitle"),
					sessions: entries.map(({ session }) => session),
					selectedSessionId: selected?.id,
					onSelectSession: setSelectedEntryId,
					insight: isLoading ? undefined : selected?.insight,
					startLabel: selected?.isResume
						? t("dashboard.recommendedTasks.resumeAction")
						: selected?.start === "test"
							? t("dashboard.recommendedTasks.startTestAction")
							: t("sessionConfig.start"),
					onStart: handleStart,
					startDisabled: isLoading || !selected,
					starting,
					loading: isLoading,
				}
			: undefined;

	return (
		<SkillDetailDialog
			visible
			onClose={onClose}
			icon={detail.icon}
			title={detail.title}
			description={detail.description}
			levels={detail.levels}
			stats={detail.stats}
			sessionSection={sessionSection}
		/>
	);
};

export default SkillDetailDialogContainer;
